/**
 * Shared date formatting utilities.
 * Format: "20-Aug-2026" / "20-Aug-2026 09:24 PM"  (dd-MMM-yyyy)
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}

// Calendar parts of an instant — in the viewer's local time, or in an IANA
// timezone when one is given (e.g. a counselor's or guest's zone).
function partsOf(d: Date, tz?: string) {
  if (!tz) return { y: d.getFullYear(), mo: d.getMonth(), day: d.getDate(), h: d.getHours(), mi: d.getMinutes() };
  try {
    const p: Record<string, string> = {};
    new Intl.DateTimeFormat('en-US', {
      timeZone: tz, year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', hourCycle: 'h23',
    }).formatToParts(d).forEach(x => { p[x.type] = x.value; });
    return { y: Number(p.year), mo: Number(p.month) - 1, day: Number(p.day), h: Number(p.hour) % 24, mi: Number(p.minute) };
  } catch {
    return partsOf(d);
  }
}

const timeOf = (h: number, mi: number) => `${pad(h % 12 || 12)}:${pad(mi)} ${h >= 12 ? 'PM' : 'AM'}`;

/** Format as "20-Aug-2026" (dd-MMM-yyyy) */
export function formatDate(date: string | Date, tz?: string): string {
  const d = new Date(date);
  if (isNaN(d.getTime())) return '—';
  const p = partsOf(d, tz);
  return `${pad(p.day)}-${MONTHS[p.mo]}-${p.y}`;
}

/** Format as "20-Aug-2026 09:24 PM" (dd-MMM-yyyy + time) */
export function formatDateTime(date: string | Date, tz?: string): string {
  const d = new Date(date);
  if (isNaN(d.getTime())) return '—';
  const p = partsOf(d, tz);
  return `${pad(p.day)}-${MONTHS[p.mo]}-${p.y} ${timeOf(p.h, p.mi)}`;
}

/** Format as "09:24 PM" */
export function formatTime(date: string | Date, tz?: string): string {
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  const p = partsOf(d, tz);
  return timeOf(p.h, p.mi);
}
